import { type ReactNode, useCallback } from 'react';

import {
  type AdminBillingTranslate,
  type BillingPlanData,
  ENDING_BILLING_STATUSES,
} from '../AdminBillingElement.types';
import { formatCurrency, formatDate, makeTranslator } from './format';

type BillingPlanCardProps = {
  plan: BillingPlanData;
  translate?: AdminBillingTranslate;
};

/**
 * Address and tax records live in Stripe, not here, so both affordances on this card
 * hand off to the same customer portal — matching the individual billing section, where
 * `View billing info` and the card's `Update` also share one handler.
 */
function useOpenStripePortal(stripePortalUrl: BillingPlanData['stripe_customer_portal_url']) {
  return useCallback(() => {
    if (stripePortalUrl) {
      window.open(stripePortalUrl, '_blank', 'noopener,noreferrer');
    }
  }, [stripePortalUrl]);
}

export function BillingPlanCard({ plan, translate }: BillingPlanCardProps) {
  const tr = makeTranslator(translate);
  const { stripe_customer_portal_url: stripePortalUrl } = plan;
  const openStripePortal = useOpenStripePortal(stripePortalUrl);
  const isOneTime = plan.interval === 'one_time';
  const cadenceLabel = isOneTime ? '' : tr(`manage.billing.plan_card.cadence.${plan.interval}`) || plan.interval;
  const amountPerCadence = tr('manage.billing.plan_card.amount_per_cadence', { cadence: cadenceLabel });

  const isOffPlatform = plan.payment_collection_method === 'off_platform';
  const isEnding = ENDING_BILLING_STATUSES.has(plan.status);
  const termStartsAt = plan.term_starts_at;
  const amountPaidSubtitle =
    termStartsAt != null ? tr('manage.billing.plan_card.amount_paid_on', { date: formatDate(termStartsAt) }) : '';

  const isGroup = plan.plan_type === 'group';
  const [groupLabelBefore, groupLabelAfter] = isGroup
    ? tr('manage.billing.plan_card.subscription_type_group_named').split('{{name}}')
    : ['', ''];

  let paymentSnapshots: ReactNode;
  if (isOffPlatform) {
    // Off-platform billing lives in an external system: no invoice, and
    // term_starts_at never advances past the original term, so anything we
    // synthesize (amount paid or next renewal) goes stale the moment the
    // publisher changes seats/price or the term rolls over. Defer to the
    // publisher with a single note instead of numbers we can't stand behind.
    paymentSnapshots = (
      <PublisherManagedCard
        title={tr('manage.billing.plan_card.off_platform_billing_title')}
        message={tr('manage.billing.plan_card.off_platform_billing_note')}
      />
    );
  } else if (isOneTime) {
    // Lifetime deal — nothing renews, so we show what was paid (the term total)
    // anchored to the term start date rather than a future date.
    paymentSnapshots = (
      <PaymentSnapshotCard
        title={tr('manage.billing.plan_card.amount_paid_title')}
        subtitle={amountPaidSubtitle}
        amount={formatCurrency(plan.total_cost_cents, plan.currency)}
        cadenceLabel=""
      />
    );
  } else {
    paymentSnapshots = (
      <>
        {/* Last Stripe payment — absent for a plan that has genuinely never been
            billed, in which case the Next payment card takes the full row. */}
        {plan.last_payment && (
          <PaymentSnapshotCard
            title={tr('manage.billing.plan_card.last_payment_title')}
            subtitle={tr('manage.billing.plan_card.last_payment_due_by', {
              date: formatDate(plan.last_payment.date),
            })}
            amount={formatCurrency(plan.last_payment.amount_cents, plan.currency)}
            cadenceLabel={amountPerCadence}
          />
        )}
        {/* Next payment is derived: the plan renews at `renews_at` for
            `total_cost_cents` per `interval` — no dedicated API field. A canceled or
            cancelling plan keeps `renews_at` (it is the term end, not a renewal), so the
            status is the only thing that distinguishes a real upcoming charge from none. */}
        {!isEnding && (
          <PaymentSnapshotCard
            title={tr('manage.billing.plan_card.next_payment_title')}
            subtitle={
              plan.renews_at
                ? tr('manage.billing.plan_card.next_payment_scheduled_for', {
                    date: formatDate(plan.renews_at),
                  })
                : ''
            }
            amount={formatCurrency(plan.total_cost_cents, plan.currency)}
            cadenceLabel={amountPerCadence}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-1">
        <p className="text-lg font-medium leading-7 text-gray-900">
          {plan.tier_name}
          <span className="ml-1 font-normal text-gray-500">{tr('manage.billing.plan_card.plan_suffix')}</span>
        </p>
        <p className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <span>
            {isGroup ? (
              <>
                {groupLabelBefore}
                {plan.group_name}
                {groupLabelAfter}
              </>
            ) : (
              tr('manage.billing.plan_card.subscription_type.individual')
            )}
          </span>
          {isGroup && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {isOneTime
                  ? tr('manage.billing.plan_card.price_per_seat_one_time', {
                      price: formatCurrency(plan.price_per_seat_cents, plan.currency),
                    })
                  : tr('manage.billing.plan_card.price_per_seat', {
                      price: formatCurrency(plan.price_per_seat_cents, plan.currency),
                      cadence: cadenceLabel,
                    })}
              </span>
            </>
          )}
        </p>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row">{paymentSnapshots}</div>

      <PaymentMethodRow plan={plan} translate={translate} onClickUpdate={openStripePortal} />

      {/* Hidden for off_platform: billing lives in an external system, so there's no
          on-platform address/tax record to disclose. */}
      {!isOffPlatform && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={openStripePortal}
            disabled={!stripePortalUrl}
            className="rounded-md px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {tr('manage.billing.plan_card.view_billing_info')}
          </button>
        </div>
      )}
    </div>
  );
}

type PaymentSnapshotCardProps = {
  title: string;
  subtitle: string;
  amount: string;
  cadenceLabel: string;
};

function PaymentSnapshotCard({ title, subtitle, amount, cadenceLabel }: PaymentSnapshotCardProps) {
  return (
    <div className="flex flex-1 flex-col gap-4 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-col">
        <p className="text-sm font-normal leading-5 text-gray-900">{title}</p>
        <p className="text-sm font-light leading-5 text-gray-600">{subtitle}</p>
      </div>
      <div className="flex items-end gap-2">
        <p className="flex-1 text-3xl font-semibold leading-9 text-gray-900">{amount}</p>
        <p className="text-sm font-light leading-5 text-gray-600">{cadenceLabel}</p>
      </div>
    </div>
  );
}

type PublisherManagedCardProps = {
  title: string;
  message: string;
};

// Snapshot-shaped card that carries a note instead of an amount — used where
// off-platform billing means we have no trustworthy figure to display.
function PublisherManagedCard({ title, message }: PublisherManagedCardProps) {
  return (
    <div className="flex flex-1 flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-4">
      <p className="text-sm font-normal leading-5 text-gray-900">{title}</p>
      <p className="text-sm font-light leading-5 text-gray-600">{message}</p>
    </div>
  );
}

type PaymentMethodRowProps = {
  plan: BillingPlanData;
  translate?: AdminBillingTranslate;
  onClickUpdate: () => void;
};

function PaymentMethodRow({ plan, translate, onClickUpdate }: PaymentMethodRowProps) {
  const tr = makeTranslator(translate);
  const { payment_method: paymentMethod, stripe_customer_portal_url: stripePortalUrl } = plan;

  const displayValue = paymentMethod
    ? `${paymentMethod.brand} ****${paymentMethod.last4}`
    : tr('manage.billing.plan_card.payment_method_empty');

  if (plan.payment_collection_method === 'off_platform') {
    const email = plan.publication_contact_email ?? '';
    const note = tr('manage.billing.plan_card.external_billing_note', { email });
    const [before, after] = email ? note.split(email) : [note, ''];
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-gray-900">{tr('manage.billing.plan_card.payment_method_label')}</p>
        <p className="text-sm text-gray-500">
          {email ? (
            <>
              {before}
              <span className="font-medium text-gray-700">{email}</span>
              {after}
            </>
          ) : (
            note
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-end gap-4">
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-sm font-medium text-gray-900">{tr('manage.billing.plan_card.payment_method_label')}</p>
        {/* Display-only — the Update button is the actionable affordance, so this
            is a styled div rather than a disabled <input> to avoid the wrong
            form-control semantics (and a phantom focus target on tab order). */}
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
          {displayValue}
        </div>
      </div>
      <button
        type="button"
        onClick={onClickUpdate}
        disabled={!stripePortalUrl}
        className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {tr('manage.billing.plan_card.update_button')}
      </button>
    </div>
  );
}
